import React, { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useJsApiLoader } from '@react-google-maps/api';
import HGFormInput from './HGFormInput';
import CountryDropdown from './CountryDropdown';
import { getCountryCodes } from './helpers';

const AddressFields = ({ disabled, ...props }) => {
    const { values, setFieldValue } = useFormikContext();
    const [libraries] = useState(['places']); // avoids unecessary re-renders warning from useJsApiLoader
    const [autocomplete, setAutocomplete] = useState(null);
    const addressRef = useRef(null);
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey,
        libraries,
    });

    useEffect(() => {
        if (isLoaded && addressRef.current && !loadError) {
            try {
                const newAutocomplete =
                    new window.google.maps.places.Autocomplete(
                        addressRef.current,
                        {
                            fields: ['address_components'],
                            componentRestrictions: { country: getCountryCodes() },
                        }
                    );
                setAutocomplete(newAutocomplete);
            } catch (error) {
                console.error('Error creating Autocomplete:', error);
            }
        }
    }, [isLoaded, addressRef, loadError]);

    useEffect(() => {
        if (autocomplete) {
            const handlePlaceChanged = () => {
                const place = autocomplete.getPlace();
                const address = place.address_components.reduce(
                    (acc, component) => {
                        const type = component.types[0];
                        switch (type) {
                            case 'street_number':
                                return {
                                    ...acc,
                                    streetNumber: component.long_name,
                                };
                            case 'route':
                                return {
                                    ...acc,
                                    streetName: component.long_name,
                                };
                            case 'subpremise': // Apartment/suite/unit number
                                return {
                                    ...acc,
                                    subpremise: component.long_name,
                                };
                            case 'locality':
                                return { ...acc, city: component.long_name };
                            case 'postal_town': // UK cities
                                return {
                                    ...acc,
                                    city: acc.city || component.long_name,
                                };
                            case 'sublocality':
                            case 'sublocality_level_1': // Neighborhoods in some countries
                                return {
                                    ...acc,
                                    city: acc.city || component.long_name,
                                };
                            case 'administrative_area_level_1': // State/Province
                                return { ...acc, state: component.short_name };
                            case 'administrative_area_level_2': // County/District fallback
                                return {
                                    ...acc,
                                    state: acc.state || component.short_name,
                                };
                            case 'postal_code_prefix':
                            case 'postal_code':
                                return {
                                    ...acc,
                                    postalCode: component.long_name,
                                };
                            case 'country':
                                return {
                                    ...acc,
                                    country: component.long_name,
                                    countryCode: component.short_name,
                                };
                            default:
                                return acc;
                        }
                    },
                    {}
                );
                if (place.address_components) {
                    const {
                        city,
                        state,
                        postalCode,
                        streetName,
                        streetNumber,
                        subpremise,
                        country,
                        countryCode,
                    } = address;

                    // Combine street number, street name, and subpremise (apt/suite/unit)
                    let fullAddress =
                        `${streetNumber ?? ''} ${streetName ?? ''}`.trim();
                    if (subpremise) {
                        fullAddress += ` ${subpremise}`;
                    }

                    setFieldValue('address', {
                        address: fullAddress,
                        city,
                        stateProvince: state || '',
                        postalCode: postalCode,
                        country: country || 'United States',
                        countryCode: countryCode || 'US',
                    });
                }
            };
            autocomplete.addListener('place_changed', handlePlaceChanged);
        }

        // Cleanup function
        return () => {
            if (autocomplete) {
                window.google.maps.event.clearInstanceListeners(autocomplete);
            }
        };
    }, [autocomplete, setFieldValue]);

    // Countries that use State/Province field
    const countriesWithStates = [
        'United States',
        'Canada',
        'Australia',
        'India',
        'Brazil',
        'Mexico',
    ];
    const showStateField = countriesWithStates.includes(
        values.address?.country || 'United States'
    );

    // Get dynamic label for State/Province field based on country
    const getStateLabel = (country) => {
        // Countries that use "Province" terminology
        const provinceCountries = ['Canada', 'Argentina', 'China'];
        if (provinceCountries.includes(country)) return 'Province';

        // All others use "State" (US, Australia, India, Brazil, Mexico)
        return 'State';
    };
    const stateLabel = getStateLabel(
        values.address?.country || 'United States'
    );

    // Clear state field when country changes to one that doesn't use states
    useEffect(() => {
        if (!showStateField && values.address?.stateProvince) {
            setFieldValue('address.stateProvince', '');
        }
    }, [
        showStateField,
        setFieldValue,
        values.address?.stateProvince,
        values.address?.country,
    ]);

    return (
        <div>
            <CountryDropdown disabled={disabled?.country} />
            <div
                className={`mt-3 gap-2 space-y-3 md:grid md:space-y-0 ${showStateField ? 'grid-cols-3' : 'grid-cols-4'}`}
            >
                <HGFormInput
                    name="address.address"
                    label="Address"
                    ref={addressRef}
                    disabled={disabled?.address}
                    className={showStateField ? 'col-span-3' : 'col-span-2'}
                />
                <HGFormInput
                    name="address.city"
                    label="City"
                    disabled={disabled?.city}
                />
                {showStateField && (
                    <HGFormInput
                        name="address.stateProvince"
                        label={stateLabel}
                        disabled={disabled?.stateProvince}
                    />
                )}
                <HGFormInput
                    name="address.postalCode"
                    label="Postal Code"
                    disabled={disabled?.postalCode}
                />
            </div>
        </div>
    );
};

export default AddressFields;
